/**
 * company-context-renderers.tsx — fabryka contextRenderers dla karty firmy.
 *
 * Mirror `document-additional-renderers.tsx` (`buildDocumentAdditionalRenderers`) —
 * osobny plik żeby wyeliminować inline'owanie w `CompanyDetail.tsx` i umożliwić
 * unit-test wiringu bez pełnego RTL mounta strony. Na start tylko jeden wpis
 * (`company_members_view`) — kolejne (np. `additional_view` po dociągnięciu
 * `companies` do `ADDITIONAL_TABLE_ALLOWED_MODULES`, patrz
 * `AdditionalTableViewElement.tsx` nagłówek) dochodzą tu, nie w stronie.
 *
 * Uwaga: importuje React (JSX), stąd rozszerzenie .tsx.
 */

import { AddressesDynamicTab } from '@/components/addresses/AddressesDynamicTab';
import { CompanyBankAccountsTab } from '@/components/bank-accounts/CompanyBankAccountsTab';
import { CompanyMembersSection } from '@/components/companies/CompanyMembersSection';
import type { ContextRenderers } from '@/components/form/RecursiveFormRenderer';
import { parseHiddenCompanyAddressColumns } from '@/components/modules/layout-elements/CompanyAddressesViewElement';
import { parseHiddenCompanyBankAccountColumns } from '@/components/modules/layout-elements/CompanyBankAccountsViewElement';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import { parseSlugList } from '@/lib/kartoteki/formatka-order';

/**
 * @param companyId id firmy (`companies.id`) — `null` w trybie create (wołający MUSI
 *   przekazać jawnie `null`, nie surowy route param: na trasie `/companies/new` `id`
 *   z `useParams` to string `'new'`, nie `undefined` — patrz `CompanyDetail.tsx`
 *   `isCreate = !id || id === 'new'`). Nie da się nadawać dostępów do niezapisanej
 *   firmy, mirror `record_links_view` bez `recordId` w `buildDocumentAdditionalRenderers`.
 */
export function buildCompanyContextRenderers(
  companyId: string | null | undefined,
): ContextRenderers {
  return {
    // Użytkownicy/role są edytowalne ZAWSZE, niezależnie od trybu edycji karty: każda zmiana roli
    // zapisuje się od razu własnym żądaniem (nie przez „Zapisz" karty). Odpięcie od globalnego
    // „Zapisz" usuwa mylący błąd walidacji „Dane" (np. NIP) przy samej zmianie ról. Uprawnienia do
    // zmiany ról pilnuje serwer.
    company_members_view: () =>
      companyId ? <CompanyMembersSection companyId={companyId} editable /> : null,
    // „Adresy firmy" jako przenośny blok (`company_addresses_view`, mirror kontrahentów).
    // `settings.hiddenColumnIds`/`columnOrder` (ikonka oka + drag w formatce bloku) sterują
    // kolumnami listy adresów.
    company_addresses_view: ({ element }) =>
      companyId ? (
        <AddressesDynamicTab
          ownerType="company"
          ownerId={companyId}
          testIdPrefix="company-addresses"
          hiddenColumnIds={parseHiddenCompanyAddressColumns(element.settings)}
          columnOrder={parseSlugList(element.settings, 'columnOrder')}
        />
      ) : null,
    // „Historia zmian" firmy jako przenośny element PER KARTOTEKA (`company_audit_view`).
    company_audit_view: () =>
      companyId ? (
        <SystemModuleAuditTab
          moduleSlug="companies"
          recordId={companyId}
          testIdPrefix="company-audit"
        />
      ) : null,
    // „Konta bankowe" firmy jako przenośny blok (`company_bank_accounts_view`, mirror kontrahentów).
    company_bank_accounts_view: ({ element }) =>
      companyId ? (
        <CompanyBankAccountsTab
          companyId={companyId}
          testIdPrefix="company-bank-accounts"
          hiddenColumnIds={parseHiddenCompanyBankAccountColumns(element.settings)}
          columnOrder={parseSlugList(element.settings, 'columnOrder')}
        />
      ) : null,
    // Fallback dla RĘCZNIE EDYTOWANYCH (non-pristine) layoutów firm sprzed blokify: zakładki
    // Adresy/Konta trzymały tam generyczny `related_records_table` (nie nazwany blok). Po zdjęciu
    // sztywnych `tabRenderers.addresses`/`bank_accounts` renderujemy je po `settings.collection`,
    // żeby stary layout nie pokazał „Brak renderera" zanim deploy re-seed nadpisze pristine.
    // NOWE layouty (po re-seedzie) mają company_addresses_view/company_bank_accounts_view, więc
    // tu nie trafiają. Inne kolekcje (org_units, F24 Pakiet 0 dawniej departments) mają
    // własny slug-renderer → tu `null`.
    related_records_table: ({ element }) => {
      if (!companyId) return null;
      const collection =
        typeof element.settings?.collection === 'string' ? element.settings.collection : '';
      if (collection === 'addresses') {
        return (
          <AddressesDynamicTab
            ownerType="company"
            ownerId={companyId}
            testIdPrefix="company-addresses"
          />
        );
      }
      if (collection === 'company_bank_accounts') {
        return (
          <CompanyBankAccountsTab companyId={companyId} testIdPrefix="company-bank-accounts" />
        );
      }
      return null;
    },
    // Fallback dla layoutów sprzed blokify (generyczny `audit_log_view` w zakładce audit_log) —
    // ten sam read-side. Po re-seedzie zakładka emituje nazwany blok (`company_audit_view`).
    audit_log_view: () =>
      companyId ? (
        <SystemModuleAuditTab
          moduleSlug="companies"
          recordId={companyId}
          testIdPrefix="company-audit"
        />
      ) : null,
  };
}
